/*
 * Styles for <user-name>: a person's display name as inline link-styled text, and the card that
 * opens over it (D-151, contact rules 6 and 8).
 *
 * Imported by username.ts via `?inline` + `unsafeCSS`. Every colour is a theme token, so dark mode
 * is tokens.css's business alone.
 *
 * The card itself is drawn in the top layer by <agwa-hover-card>, so nothing here stacks it.
 *
 * The name sits inline in running text, table cells, bylines and badges, so it takes the font of
 * whatever it sits in and adds only the link colour and underline. On a coloured fill the host
 * sets on-fill and the name keeps the fill's text colour.
 */

:host {
  display: inline;
}

.user-name-wrapper {
  display: inline;
}

/* A button that reads as a link: it opens a card rather than navigating (see username.ts). */
.user-name-link {
  all: unset;
  display: inline;
  cursor: pointer;
  color: var(--agwa-accent-text);
  text-decoration: underline;
  text-decoration-thickness: 1px;
  text-underline-offset: 2px;
  font: inherit;
  overflow-wrap: anywhere;
}
:host([on-fill]) .user-name-link {
  color: inherit;
}
.user-name-link:hover {
  text-decoration-thickness: 2px;
}
.user-name-link:focus-visible {
  outline: 2px solid var(--agwa-accent);
  outline-offset: 2px;
  border-radius: 2px;
}
/* A deleted account is struck through wherever it is named; the card says which state it is in. */
.user-name-link.state-deleted {
  text-decoration-line: underline line-through;
}

.user-name-unknown,
.user-name-pending {
  color: var(--agwa-text-muted);
}

.user-card {
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
  min-width: 14rem;
  max-width: 22rem;
  padding: 0.75rem 0.9rem;
  background: var(--agwa-surface-raised);
  color: var(--agwa-text);
  border: 1px solid var(--agwa-border);
  border-radius: var(--sl-border-radius-medium);
  box-shadow: var(--agwa-shadow-lg);
  font-size: var(--sl-font-size-small);
  line-height: 1.4;
  text-align: start;
  white-space: normal;
}
.user-card-avatar {
  --size: 48px;
  flex: 0 0 auto;
}
.user-card-details {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  min-width: 0;
}
.user-card-name {
  font-weight: 600;
  font-size: var(--sl-font-size-medium);
  overflow-wrap: anywhere;
}
.user-card-username,
.user-card-status {
  color: var(--agwa-text-muted);
  overflow-wrap: anywhere;
}
.user-card-email,
.user-card-phone {
  color: var(--agwa-accent-text);
  overflow-wrap: anywhere;
}
